// The frame around log in and sign up: one centred column (headline, then the form in a white card) on a
// light page lit by the brand's two glows.
import styled, { createGlobalStyle } from "styled-components";
import { MARKETING } from "../theme/ThemeContext";
import { LOGO, SANS, SERIF } from "../pages/LandingPage";

const C = MARKETING;

const AuthGlobal = createGlobalStyle`
  html, body {
    margin: 0;
    background: ${C.wash} !important;
    font-family: ${SANS};
    color: ${C.ink};
    -webkit-font-smoothing: antialiased;
  }
`;

const Shell = styled.div`
  position: relative;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background:
    radial-gradient(ellipse 900px 620px at 8% -10%, color-mix(in srgb, ${C.blue} 16%, transparent), transparent 70%),
    radial-gradient(ellipse 760px 560px at 96% 108%, color-mix(in srgb, ${C.orange} 15%, transparent), transparent 70%),
    linear-gradient(180deg, ${C.onDark} 0%, ${C.wash} 100%);
`;

const Top = styled.header`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: clamp(22px, 3vw, 34px) clamp(20px, 4vw, 48px) 0;
  a {
    display: inline-flex;
    text-decoration: none;
  }
  img {
    height: 26px;
    width: auto;
    display: block;
  }
  .back {
    font-size: 13.5px;
    color: ${C.muted};
    &:hover {
      color: ${C.ink};
    }
  }
`;

const Middle = styled.main`
  position: relative;
  z-index: 2;
  flex: 1;
  /* Exactly one viewport tall, padding included, so the column is centred on the screen itself. */
  box-sizing: border-box;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Equal top and bottom clearance for the floating header and footer. */
  padding: 104px 20px;
  @media (max-width: 560px) {
    padding: 88px 20px;
  }
`;

const Column = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: ${({ $wide }) => ($wide ? "680px" : "560px")};
`;

const Words = styled.div`
  text-align: center;
  margin: 0 auto 30px;
  max-width: 640px;
  h1 {
    font-family: ${SERIF};
    font-weight: 400;
    font-size: clamp(36px, 4.6vw, 56px);
    line-height: 1.05;
    letter-spacing: -0.015em;
    color: ${C.deep};
    margin: 0;
    text-wrap: balance;
  }
  p {
    margin: 14px auto 0;
    font-size: 16.5px;
    line-height: 1.55;
    max-width: 44ch;
    color: ${C.body};
    text-wrap: pretty;
  }
  @media (max-width: 560px) {
    h1 {
      font-size: 32px;
    }
    p {
      font-size: 15px;
    }
  }
`;

const Card = styled.div`
  background: ${C.onDark};
  border-radius: 24px;
  border: 1px solid ${C.line};
  box-shadow: 0 50px 110px -40px color-mix(in srgb, ${C.deep} 36%, transparent);
  padding: 40px 48px 38px;
  @media (max-width: 560px) {
    padding: 26px 20px 24px;
    border-radius: 18px;
  }
`;

const Foot = styled.footer`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 3;
  display: flex;
  gap: 16px;
  justify-content: center;
  padding: 0 20px 26px;
  font-size: 12.5px;
  color: ${C.muted};
  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      color: ${C.ink};
    }
  }
`;

export default function AuthShell({ headline, sub, homeHref = "/", backLabel = "Back to site", wide = false, children }) {
  return (
    <Shell>
      <AuthGlobal />
      <Top>
        <a href={homeHref} aria-label="scoutm8 home">
          <img src={LOGO} alt="scoutm8" />
        </a>
        <a className="back" href={homeHref}>
          {backLabel}
        </a>
      </Top>
      <Middle>
        <Column $wide={wide}>
          <Words>
            <h1>{headline}</h1>
            {sub && <p>{sub}</p>}
          </Words>
          <Card>{children}</Card>
        </Column>
      </Middle>
      <Foot>
        <a href="/terms">Terms</a>
        <a href="/privacy-policy">Privacy</a>
      </Foot>
    </Shell>
  );
}
